import React, { useEffect, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import theme from 'styles/theme';
import { Chip, Column, Row } from 'components/pages/market/home/MarketItem';
import PhoneSvg from 'assets/icons/phone.svg';
import ChatBubbleSvg from 'assets/icons/chat_bubble.svg';
import BigHeartBorder from 'assets/icons/big_heart_border.svg';
import BigHeart from 'assets/icons/big_heart.svg';
import Modal from 'components/common/modal/Modal';
import SimpleTextModalContents from 'components/pages/market/detail/SimpleTextModalContents';
import ProceedableModalContents from 'components/pages/market/detail/ProceedableModalContents';
import useMarket from 'hooks/useMarket';
import { useNavigate } from 'react-router-dom';
import useUser from 'hooks/useUser';
import { getUserId, MOCK_USER_ID } from '../home/MarketHome';

function MarketDetailTopInfo({ info }) {
   // constants
   const CALL_TEXT = '전화';
   const SMS_TEXT = '채팅';
   const LIKE_TEXT = '찜';
   const GUIDE_TEXT = '문의 시 ‘플로웨이에서 보고 연락드려요~’라고 문의해 주세요.';
   const PREPARE_ENTER_MARKET_TEXT = '도매상 입점 준비 중이에요';
   const NEED_REGISTER_BUSINESS_TEXT =
      '도매상 문의하기는 사업자 인증을 완료한\n회원만 이용할 수 있는 기능이에요.';
   const CANCEL_TEXT = '닫기';
   const PROCEED_TEXT = '사업자 인증하기';
   const WELCOME_TEXT = "안녕하세요. 플로웨이에서 보고 연락드려요.\n";

   // states
   const [isLike, setLike] = useState(false);
   const [likeCount, setLikeCount] = useState(0);
   const [isBusinessRegistered, setBusinessRegister] = useState(false); // mock
   const [isOpenModal, setOpenModal] = useState(false);
   const [isOpenVerifyModal, setOpenVerifyModal] = useState(false);

   // handlers
   const openModal = () => setOpenModal(true);
   const closeModal = () => setOpenModal(false);
   const openVerifyModal = () => setOpenVerifyModal(true);
   const closeVerifyModal = () => setOpenVerifyModal(false);
   const onProceedVerify = () => {
      navigate('/signup');
   };
   const navigateToPhone = phoneNo => window.open(`tel:${phoneNo}`);
   const navigateToSMS = phoneNo =>
      window.open(`sms:${phoneNo}?body=${WELCOME_TEXT}`);
   const handleLike = () => {
      // const userLevel = localStorage.getItem('level');
      const userId = getUserId();
      const isRegisteredUser = userId !== MOCK_USER_ID;

      if (isRegisteredUser) {
         if (isLike) {
            unlikeMarket({ wholeId: info.whole_id });
            setLike(false);
            if (likeCount > 0) {
               setLikeCount(likeCount - 1);
            }
         } else {
            likeMarket({ wholeId: info.whole_id });
            setLike(true);
            setLikeCount(likeCount + 1);
         }
      } else {
         navigate('/login');
      }
   };
   const validatePhone = phoneNo => validateMerchant(phoneNo, () => navigateToPhone(phoneNo));
   const validateSMS = phoneNo => validateMerchant(phoneNo, () => navigateToSMS(phoneNo));
   const validateMerchant = (value, onSuccess) => {
      if (value) {
         if (isBusinessRegistered) {
            onSuccess();
         } else {
            openVerifyModal();
         }
      } else {
         openModal();
      }
   };

   // hooks
   const { likeMarket, unlikeMarket } = useMarket();
   const { user } = useUser();
   const navigate = useNavigate();
   useEffect(() => {
      const userLevel = localStorage.getItem('level');
      const isBusinessRegistered = typeof userLevel === 'number' && userLevel <= 1; // 관리자도 모두 가능하게함.(일반만 불가)
      setBusinessRegister(isBusinessRegistered);
   }, []);
   useLayoutEffect(() => {
      if (!info) return;
      setLike(info.is_liked === 1);
      setLikeCount(info.favor_cnt ? info.favor_cnt : 0);
   }, [info]);

   if (!info) {
      return <></>;
   }
   return (
      <MarketInfoWrap>
         <MarketImage src={info.whole_img} alt={info.whole_name} />
         <MarketInfo>
            <MarketName>{info.whole_name}</MarketName>
            <MarketDescription>{info.whole_loc}</MarketDescription>
            <TagsWrapper>
               {info.items_on_sale.map((tag, index) => {
                  return <Chip key={`${index}-${tag}`}>{tag}</Chip>;
               })}
            </TagsWrapper>
            <ButtonsWrap>
               <ActionBtn onClick={() => validatePhone(info.wholeTel)}>
                  <ActionIcon src={PhoneSvg} /> {CALL_TEXT}
               </ActionBtn>
               <ActionBtn onClick={() => validateSMS(info.wholeTel)}>
                  <ActionIcon src={ChatBubbleSvg} />
                  {SMS_TEXT}
               </ActionBtn>
               <ActionBtn onClick={handleLike}>
                  <ActionIcon src={isLike ? BigHeart : BigHeartBorder} />
                  {LIKE_TEXT} {likeCount}
               </ActionBtn>
            </ButtonsWrap>
            <GuideBox>{GUIDE_TEXT}</GuideBox>
         </MarketInfo>

         {/* modals */}
         <Modal isOpen={isOpenModal} onOpen={openModal} onClose={closeModal}>
            <SimpleTextModalContents text={PREPARE_ENTER_MARKET_TEXT} onClose={closeModal} />
         </Modal>

         <Modal isOpen={isOpenVerifyModal} onOpen={openVerifyModal} onClose={closeVerifyModal}>
            <ProceedableModalContents
               text={NEED_REGISTER_BUSINESS_TEXT}
               onClose={closeVerifyModal}
               onProceed={onProceedVerify}
               closeText={CANCEL_TEXT}
               proceedText={PROCEED_TEXT}
            />
         </Modal>
      </MarketInfoWrap>
   );
}

export default MarketDetailTopInfo;

const MarketInfoWrap = styled.section`
   display: flex;
   flex-direction: column;
   margin-bottom: 8px;
   background-color: ${theme.color.white};
`;

const MarketImage = styled.img`
   aspect-ratio: 9/5;
   width: 100%;
   object-fit: cover;
`;

const MarketName = styled.p`
   margin-bottom: 12px;
   font-weight: 700;
   font-size: 20px;
   line-height: 28px;
`;
const MarketDescription = styled.p`
   margin-bottom: 12px;
   font-weight: 400;
   font-size: 14px;
   line-height: 20px;
   color: ${theme.color.gray600};
`;
const TagsWrapper = styled(Row)`
   gap: 8px;
   flex-wrap: wrap;
   margin-bottom: 16px;

   ${Chip} {
      width: fit-content;
   }
`;

const ButtonsWrap = styled(Row)`
   justify-content: space-between;
   gap: 9px;
   margin-bottom: 12px;
`;

const ActionBtn = styled.button`
   cursor: pointer;
   display: flex;
   flex-direction: row;
   align-items: center;
   justify-content: center;
   border: 1px solid ${theme.color.gray300};
   padding: 12px;
   width: 100%;
   font-family: 'Pretendard';
   font-weight: 400;
   font-size: 14px;
   line-height: 20px;
   background-color: ${theme.color.white};
   border-radius: 8px;
   text-align: center;
`;

const ActionIcon = styled.img`
   width: 16px;
   height: 16px;
   margin-right: 4px;
`;
const MarketInfo = styled(Column)`
   padding: 24px 16px;
`;

const GuideBox = styled.div`
   padding: 8px 12px;
   background-color: ${theme.color.gray100};
   border-radius: 6px;
   font-weight: 400;
   font-size: 13px;
   line-height: 20px;
   letter-spacing: -0.4px;
`;
